// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * L4 of the ontology typing layers — the one place the wire's leniency becomes
 * type safety. See docs/power-flow/power-flow-ontology-typing.md.
 *
 * `types/digitalTwin.ts` (L1) stays deliberately open: `type` is a `string` and
 * `state` has an index signature, because the BFF passes unknown entity types
 * and undeclared measurements straight through by design. This module is where
 * that open shape is narrowed against the generated vocabulary (L2), and where
 * a measurement name becomes checkable against the entity type it is read from
 * rather than being a bare string.
 *
 * Two families of read, and the difference matters:
 *
 *   readDeclaredMeasurement(entity, name)  the name must be one the entity's
 *                                          type declares — a typo is a compile
 *                                          error. Use this wherever the type is
 *                                          known.
 *   readMeasurement(entity, name)          open form, for the
 *                                          undeclared-but-published keys the
 *                                          schema legalises via
 *                                          `additionalProperties`.
 */

import type { Entity, EntityState, Relationship, ValueEnvelope } from '../types/digitalTwin';
import {
  DECLARED_ATTRIBUTES,
  DECLARED_MEASUREMENTS,
  ENTITY_TYPE_NAMES,
  MEASUREMENT_NAMES,
  MEASUREMENT_UNITS,
  RELATIONSHIP_INVERSE,
  RELATIONSHIP_TYPE_NAMES,
} from '../types/generated/ontology.generated';
import type {
  AttributeNameOf,
  EntityTypeName,
  InverseOf,
  MeasurementName,
  RelationshipTypeName,
  StateKeyOf,
  UnitOf,
} from '../types/generated/ontology.generated';

const ENTITY_TYPES = new Set<string>(ENTITY_TYPE_NAMES);
const MEASUREMENTS = new Set<string>(MEASUREMENT_NAMES);
const RELATIONSHIP_TYPES = new Set<string>(RELATIONSHIP_TYPE_NAMES);

const ATTRIBUTES: Partial<Record<string, readonly string[]>> = DECLARED_ATTRIBUTES;
const STATE_KEYS: Partial<Record<string, readonly string[]>> = DECLARED_MEASUREMENTS;
const UNITS: Partial<Record<string, readonly string[]>> = MEASUREMENT_UNITS;
const INVERSES: Partial<Record<string, RelationshipTypeName>> = RELATIONSHIP_INVERSE;

/** Keys that live in `state` but are envelope metadata, not measurements. */
export const STATE_METADATA_KEYS: readonly string[] = ['updatedAt', 'status'];

/**
 * An entity narrowed to a known ontology type, with its declared measurements
 * spelled out. Every one is optional: the schema's `required` lists are
 * contradicted by real payloads in both directions, so a non-optional
 * measurement would be a lie the compiler helps you believe. The `EntityState`
 * intersection keeps the index signature, so undeclared-but-published keys stay
 * reachable through `readMeasurement`.
 */
export interface TypedEntity<T extends EntityTypeName> extends Entity {
  type: T;
  state?: EntityState & Partial<Record<StateKeyOf<T>, ValueEnvelope>>;
}

// ---------------------------------------------------------------------------
// Membership — the boundary between the open wire and the closed vocabulary
// ---------------------------------------------------------------------------

export const isKnownEntityType = (type: string): type is EntityTypeName => ENTITY_TYPES.has(type);

export const isKnownMeasurement = (name: string): name is MeasurementName => MEASUREMENTS.has(name);

export const isKnownRelationshipType = (type: string): type is RelationshipTypeName =>
  RELATIONSHIP_TYPES.has(type);

// ---------------------------------------------------------------------------
// Narrowing
// ---------------------------------------------------------------------------

export const isEntityType = <T extends EntityTypeName>(
  entity: Entity,
  type: T
): entity is TypedEntity<T> => entity.type === type;

/**
 * Narrows against several types at once. `T` is inferred from the array's
 * elements, so `isAnyEntityType(e, ['UPSType', 'PDUType'])` narrows to exactly
 * those two rather than to `EntityTypeName`.
 */
export const isAnyEntityType = <T extends EntityTypeName>(
  entity: Entity,
  types: readonly T[]
): entity is TypedEntity<T> => (types as readonly string[]).includes(entity.type);

/** The entities of one ontology type, narrowed. */
export const entitiesOfType = <T extends EntityTypeName>(
  entities: readonly Entity[] | undefined,
  type: T
): TypedEntity<T>[] =>
  (entities ?? []).filter((entity): entity is TypedEntity<T> => isEntityType(entity, type));

// ---------------------------------------------------------------------------
// What the ontology declares
// ---------------------------------------------------------------------------

/**
 * The ontology's declared inverse for a predicate. Typed for a known predicate
 * (`inverseOf('feeds')` is `'fedBy'`, not `RelationshipTypeName | undefined`),
 * and still callable with an arbitrary string off the wire.
 */
export function inverseOf<T extends RelationshipTypeName>(type: T): InverseOf<T>;
export function inverseOf(type: string): RelationshipTypeName | undefined;
export function inverseOf(type: string): RelationshipTypeName | undefined {
  return isKnownRelationshipType(type) ? INVERSES[type] : undefined;
}

export function declaredAttributesOf<T extends EntityTypeName>(
  entityType: T
): readonly AttributeNameOf<T>[];
export function declaredAttributesOf(entityType: string): readonly string[];
export function declaredAttributesOf(entityType: string): readonly string[] {
  return isKnownEntityType(entityType) ? (ATTRIBUTES[entityType] ?? []) : [];
}

export function declaredMeasurementsOf<T extends EntityTypeName>(
  entityType: T
): readonly StateKeyOf<T>[];
export function declaredMeasurementsOf(entityType: string): readonly string[];
export function declaredMeasurementsOf(entityType: string): readonly string[] {
  return isKnownEntityType(entityType) ? (STATE_KEYS[entityType] ?? []) : [];
}

export const declaresMeasurement = (entityType: string, name: string): boolean =>
  declaredMeasurementsOf(entityType).includes(name);

/** The unit codes a measurement declares, or `[]` where it declares none. */
export const declaredUnitsOf = (name: string): readonly string[] =>
  isKnownMeasurement(name) ? (UNITS[name] ?? []) : [];

export const isDeclaredUnit = (name: string, unit: string): boolean =>
  declaredUnitsOf(name).includes(unit);

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

const isEnvelope = (value: unknown): value is ValueEnvelope =>
  typeof value === 'object' && value !== null && 'value' in value;

/**
 * Open read, for any state key. Use it for the undeclared-but-published
 * measurements the schema legalises via `additionalProperties` — the PDU's
 * `LoadingPercentage`, the feeder's `PrimaryKV` — and where the entity type is
 * not statically known. Where it is, prefer `readDeclaredMeasurement`.
 */
export const readMeasurement = (
  entity: Entity | undefined,
  name: string
): ValueEnvelope | undefined => {
  const raw = entity?.state?.[name];
  return isEnvelope(raw) ? raw : undefined;
};

/**
 * Checked read: `name` must be a state key the entity's own type declares, so
 * `readDeclaredMeasurement(ups, 'Frequenncy')` and
 * `readDeclaredMeasurement(ups, 'BusbarRating')` are both compile errors rather
 * than silent `undefined`s.
 */
export const readDeclaredMeasurement = <T extends EntityTypeName, K extends StateKeyOf<T>>(
  entity: TypedEntity<T> | undefined,
  name: K
): ValueEnvelope | undefined => readMeasurement(entity, name as string);

/**
 * A finite number, or `undefined`. Never `0` for an absent measurement — the
 * distinction between "reporting zero" and "not reporting" is the whole point
 * of the unavailable state.
 */
export const readMeasurementNumber = (
  entity: Entity | undefined,
  name: string
): number | undefined => {
  const value = readMeasurement(entity, name)?.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

/**
 * A number *in the unit asked for*, or `undefined`. Does not convert: a
 * measurement published in `MegaW` read as `KiloW` is `undefined`, not a value
 * a thousand times too small. The unit argument is checked against what the
 * measurement declares, so `readMeasurementInUnit(e, 'ActivePower', 'DEG_C')`
 * does not compile.
 */
export const readMeasurementInUnit = <M extends MeasurementName>(
  entity: Entity | undefined,
  name: M,
  unit: UnitOf<M>
): number | undefined => {
  const envelope = readMeasurement(entity, name);
  if (!envelope || envelope.unit !== (unit as string)) return undefined;
  return typeof envelope.value === 'number' && Number.isFinite(envelope.value)
    ? envelope.value
    : undefined;
};

/** The state keys a payload actually carries, metadata excluded, sorted. */
export const publishedMeasurementsOf = (entity: Entity | undefined): string[] =>
  Object.keys(entity?.state ?? {})
    .filter(key => !STATE_METADATA_KEYS.includes(key))
    .sort();

/**
 * Measurements a type declares and this payload does not carry. The diagnostic
 * behind PR 5's "expected here, not published" — note it reports against what
 * the ontology *declares*, which is a weaker claim than the SHACL profile's
 * statement of what is expected.
 */
export const undeclaredGapOf = (entity: Entity | undefined): string[] => {
  if (!entity) return [];
  const published = new Set(publishedMeasurementsOf(entity));
  return declaredMeasurementsOf(entity.type)
    .filter(name => !published.has(name))
    .slice()
    .sort();
};

// ---------------------------------------------------------------------------
// The counter
// ---------------------------------------------------------------------------

export interface UnknownVocabulary {
  entityTypes: string[];
  relationshipTypes: string[];
  measurements: string[];
  units: string[];
  clean: boolean;
}

/**
 * Everything in a payload the ontology does not describe.
 *
 * This is not a nicety. A namespaced predicate (`brick:feeds` rather than
 * `feeds`) would be dropped by `normalizeFlowRelationship` and the diagram
 * would lose conductors with no error at all; counting unknown predicates is
 * what makes that fail loudly instead.
 */
export const unknownVocabulary = (graph: {
  entities?: readonly Entity[];
  relationships?: readonly Relationship[];
}): UnknownVocabulary => {
  const entityTypes = new Set<string>();
  const relationshipTypes = new Set<string>();
  const measurements = new Set<string>();
  const units = new Set<string>();

  for (const entity of graph.entities ?? []) {
    if (!isKnownEntityType(entity.type)) entityTypes.add(entity.type);

    for (const name of publishedMeasurementsOf(entity)) {
      if (!isKnownMeasurement(name)) {
        measurements.add(name);
        continue;
      }
      const unit = readMeasurement(entity, name)?.unit;
      const declared = declaredUnitsOf(name);
      if (unit && declared.length > 0 && !declared.includes(unit)) {
        units.add(`${name}:${unit}`);
      }
    }
  }

  for (const relationship of graph.relationships ?? []) {
    if (!isKnownRelationshipType(relationship.type)) relationshipTypes.add(relationship.type);
  }

  const sort = (values: Set<string>) => Array.from(values).sort();
  const found = {
    entityTypes: sort(entityTypes),
    relationshipTypes: sort(relationshipTypes),
    measurements: sort(measurements),
    units: sort(units),
  };

  return { ...found, clean: Object.values(found).every(list => list.length === 0) };
};
